import React, { useState, useEffect, useMemo } from "react";
import { parseCurrency, formatCurrency, formatCurrencyInput } from "../../utils/currency";
import axios from "axios";
import { toast } from "react-hot-toast";
import { FaFilter, FaPlus, FaChevronDown, FaChevronUp, FaEdit, FaTrash, FaMoneyBillWave, FaChartPie, FaChartLine, FaArrowUp, FaArrowDown, FaSortAmountUp, FaSortAmountDown } from "react-icons/fa";
import { Line, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Chart.js bileşenlerini kaydet
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Modal Bileşeni (Yeni Kayıt/Düzenleme için)
const RecordModal = ({ isOpen, onClose, onSave, record }) => {
  const [formData, setFormData] = useState(record || {
    description: '',
    amount: '',
    transaction_date: new Date().toISOString().slice(0, 10), // Varsayılan bugün
    payment_method: 'nakit',
    status: 'completed',
    category: '',
    notes: ''
  });

  const [recordType, setRecordType] = useState(record && record.amount ? 
    (parseCurrency(record.amount) >= 0 ? 'Gelir' : 'Gider') : 'Gider');

  useEffect(() => {
    // Kayıt düzenleniyorsa formu doldur
    if (record) {
      // Gelir/Gider tipini belirle
      const recordTypeVal = parseFloat(record.amount) >= 0 ? 'Gelir' : 'Gider'; 
      setRecordType(recordTypeVal);
      
      setFormData({
        ...record,
        // amount değerini her zaman pozitif göster ve formatla
        amount: formatCurrency(Math.abs(parseFloat(record.amount))),
        transaction_date: record.transaction_date || new Date().toISOString().slice(0, 10),
        category: record.category || ''
      });
    } else {
      // Yeni kayıt için formu sıfırla
      setFormData({
        description: '',
        amount: '',
        transaction_date: new Date().toISOString().slice(0, 10),
        payment_method: 'nakit',
        status: 'completed',
        category: '',
        notes: ''
      });
      setRecordType('Gider');
    }
  }, [record, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "amount") {
      const formatted = formatCurrencyInput(value);
      setFormData({ ...formData, [name]: formatted });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleTypeChange = (type) => {
    setRecordType(type);
  };

  const handleAmountBlur = (e) => {
    const value = e.target.value;
    if (value && value !== '') {
      const numValue = parseCurrency(value);
      if (!isNaN(numValue)) {
        const formatted = formatCurrency(numValue);
        setFormData({ ...formData, amount: formatted });
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.description || !formData.amount || !formData.transaction_date) {
      toast.error("Lütfen tüm zorunlu alanları doldurun.");
      return;
    }
    
    // Gelir/Gider tipine göre tutarı ayarla
    const amountNumber = parseCurrency(formData.amount);
    if (isNaN(amountNumber)) {
      toast.error("Geçerli bir tutar giriniz");
      return;
    }

    const amount = recordType === 'Gelir' 
      ? Math.abs(amountNumber) 
      : -Math.abs(amountNumber);
      
    onSave({
      ...formData,
      amount
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-800">{record ? "Kaydı Düzenle" : "Yeni Kayıt Ekle"}</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tür</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleTypeChange('Gelir')}
                className={`py-2 px-4 rounded-lg border text-center ${
                  recordType === 'Gelir' 
                    ? 'bg-green-100 border-green-500 text-green-800' 
                    : 'border-gray-300 text-gray-600'
                }`}
              >
                Gelir
              </button>
              <button
                type="button"
                onClick={() => handleTypeChange('Gider')}
                className={`py-2 px-4 rounded-lg border text-center ${
                  recordType === 'Gider' 
                    ? 'bg-red-100 border-red-500 text-red-800' 
                    : 'border-gray-300 text-gray-600'
                }`}
              >
                Gider
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
            <input
              type="text"
              name="description"
              placeholder="Örn: Kırtasiye alımı, Maaş ödemesi..."
              value={formData.description}
              onChange={handleChange}
              required
              className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tutar (₺)</label>
            <input
              type="text"
              name="amount"
              placeholder="0,00"
              value={formData.amount}
              onChange={handleChange}
              onBlur={handleAmountBlur}
              inputMode="decimal"
              required
              className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tarih</label>
            <input
              type="date"
              name="transaction_date"
              value={formData.transaction_date}
              onChange={handleChange}
              required
              className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
              min="1900-01-01"
              max="2099-12-31"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ödeme Yöntemi</label>
            <select 
              name="payment_method"
              value={formData.payment_method}
              onChange={handleChange}
              className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="nakit">Nakit</option>
              <option value="kart">Kredi Kartı</option>
              <option value="havale">Havale/EFT</option>
              <option value="diger">Diğer</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
            <input
              type="text"
              name="category"
              placeholder="Örn: Kira, Maaş, Öğrenci Ödemesi..."
              value={formData.category}
              onChange={handleChange}
              className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Not (Opsiyonel)</label>
            <textarea
              name="notes"
              rows="3"
              placeholder="Kayıtla ilgili notlar..."
              value={formData.notes || ""}
              onChange={handleChange}
              className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
            ></textarea>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="py-2 px-4 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
            >
              İptal
            </button>
            <button
              type="submit"
              className="py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const FinancialPage = () => {
  const [records, setRecords] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortOrder, setSortOrder] = useState({ field: 'date', direction: 'desc' });
  const [filters, setFilters] = useState({ 
    type: 'Hepsi', 
    category: '', 
    dateRange: { start: '', end: '' },
    quickFilter: 'buAy' // Varsayılan olarak Bu Ay
  });
  const [showFilters, setShowFilters] = useState(false);
  const [filterCount, setFilterCount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  


  useEffect(() => {
    fetchRecords();
    fetchStudents();
    // Sayfa ilk açıldığında Bu Ay filtresini uygula
    applyQuickDateFilter('buAy');
  }, []);

  useEffect(() => {
    // Aktif filtre sayısını hesapla
    let count = 0;
    if (filters.type !== 'Hepsi') count++;
    if (filters.category) count++;
    if (filters.dateRange.start || filters.dateRange.end) count++;
    setFilterCount(count);
  }, [filters]);

  // Hızlı tarih filtresi uygulama fonksiyonu
  const applyQuickDateFilter = (filterType) => {
    const now = new Date();
    let startDate = '';
    let endDate = '';
    
    switch(filterType) {
      case 'bugun':
        // Bugün - günün başından sonuna kadar
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        startDate.setHours(0, 0, 0, 0); // Günün başı
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        endDate.setHours(23, 59, 59, 999); // Günün sonu
        break;
      case 'buAy':
        // Ay başı
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        // Ay sonu - günün son saatine (23:59:59) ayarlanmış hali
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        endDate.setHours(23, 59, 59, 999); // Günün son anına ayarla
        break;
      case 'son3Ay':
        // 3 ay öncesi
        startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        // Bu ayın sonu - günün son saatine ayarla
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        endDate.setHours(23, 59, 59, 999); // Günün son anına ayarla
        break;
      case 'son6Ay':
        // 6 ay öncesi
        startDate = new Date(now.getFullYear(), now.getMonth() - 5, 1);
        // Bu ayın sonu - günün son saatine ayarla
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        endDate.setHours(23, 59, 59, 999); // Günün son anına ayarla
        break;
      case 'tumZaman':
        // Filtre yok, tüm zamanlar
        startDate = '';
        endDate = '';
        break;
      default:
        break;
    }
    
    setFilters(prev => ({
      ...prev,
      quickFilter: filterType,
      dateRange: {
        start: startDate ? startDate.toISOString().slice(0, 10) : '',
        end: endDate ? endDate.toISOString().slice(0, 10) : ''
      }
    }));
  };

  // Aktif filtre durumunu gösteren fonksiyon
  const getActiveFilterText = () => {
    const now = new Date();
    
    switch(filters.quickFilter) {
      case 'bugun':
        return `Bugün (${now.toLocaleDateString('tr-TR')})`;
      case 'buAy':
        const monthName = new Intl.DateTimeFormat('tr-TR', { month: 'long' }).format(now);
        return `${monthName} ${now.getFullYear()}`;
      case 'son3Ay':
        const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        const threeMonthsAgoName = new Intl.DateTimeFormat('tr-TR', { month: 'long' }).format(threeMonthsAgo);
        return `${threeMonthsAgoName} - ${new Intl.DateTimeFormat('tr-TR', { month: 'long' }).format(now)} ${now.getFullYear()}`;
      case 'son6Ay':
        const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1);
        const sixMonthsAgoName = new Intl.DateTimeFormat('tr-TR', { month: 'long' }).format(sixMonthsAgo);
        return `${sixMonthsAgoName} - ${new Intl.DateTimeFormat('tr-TR', { month: 'long' }).format(now)} ${now.getFullYear()}`;
      case 'tumZaman':
        return "Tüm Zamanlar";
      case 'ozel':
        if (filters.dateRange.start && filters.dateRange.end) {
          const startDate = new Date(filters.dateRange.start);
          const endDate = new Date(filters.dateRange.end);
          return `${startDate.toLocaleDateString('tr-TR')} - ${endDate.toLocaleDateString('tr-TR')}`;
        } else if (filters.dateRange.start) {
          const startDate = new Date(filters.dateRange.start);
          return `${startDate.toLocaleDateString('tr-TR')}'den itibaren`;
        } else if (filters.dateRange.end) {
          const endDate = new Date(filters.dateRange.end);
          return `${endDate.toLocaleDateString('tr-TR')}'e kadar`;
        }
        return "Özel Tarih Aralığı";
      default:
        return "Tüm Veriler";
    }
  };

  const fetchRecords = async () => {
    setLoading(true);
    setError(null);

    try {
      // Local storage'dan token al
      const token = localStorage.getItem('authToken');
      
      // Authorization başlığı için headers hazırla
      const headers = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else {
        console.error('Token bulunamadı! Lütfen giriş yapın.');
        setError('Oturum süresi dolmuş olabilir. Lütfen tekrar giriş yapın.');
        setLoading(false);
        return;
      }

      console.log('API isteği gönderiliyor...');
      const timestamp = new Date().getTime();
      const response = await axios.get(`http://esnakademi.com/api/financial-records?_t=${timestamp}`, { headers });
      console.log('API yanıtı alındı:', response);

      // Response data kontrolü
      let recordData = [];
      
      if (typeof response.data === 'string') {
        // Eğer "Tabloda veri yok." mesajı ise boş array kullan
        if (response.data === 'Tabloda veri yok.') {
          recordData = [];
          console.log('Veritabanında kayıt bulunamadı, boş array kullanılıyor');
        } else {
          // String cevap durumunda parse etmeyi dene
          try {
            recordData = JSON.parse(response.data);
            console.log('String veri parse edildi:', recordData);
          } catch (parseError) {
            console.error('String veri parse hatası:', parseError);
            setError('Veri format hatası oluştu');
          }
        }
      } else if (Array.isArray(response.data)) {
        // Array ise direkt kullan
        recordData = response.data;
        console.log('Array veri alındı:', recordData);
      } else if (typeof response.data === 'object') {
        // Object ise, data property'si varsa kullan
        recordData = response.data.data || response.data;
        console.log('Object veri alındı:', recordData);
      }

      // Tarih alanlarını düzelt ve type/category alanlarını ekle
      recordData = recordData.map(record => ({
        ...record,
        date: new Date(record.transaction_date),
        type: parseCurrency(record.amount) >= 0 ? 'Gelir' : 'Gider',
        // Backend'den gelen category alanını kullan, yoksa description'ı kullan
        category: record.category || record.description,
        // Amount'u orijinal değerini koruyarak formatla (negatif değerler gider için önemli)
        amount: parseFloat(record.amount)
      }));

      setRecords(recordData);
    } catch (error) {
      console.error('Finansal kayıtları getirirken hata:', error.response || error);
      setError(`Finansal kayıtlar yüklenemedi: ${error.response?.data?.message || error.message}`);
      
      // HTML hata mesajı kontrolü
      if (error.response?.data && typeof error.response.data === 'string' && error.response.data.includes('<!DOCTYPE html>')) {
        setError('Sunucu hatası: Backend uygulaması hata döndürdü');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const timestamp2 = new Date().getTime();
      const res = await axios.get(`http://esnakademi.com/api/students?_t=${timestamp2}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStudents(res.data);
    } catch (err) {
      console.error("Öğrenci verileri alınamadı", err);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleDateRangeChange = (field, value) => {
    setFilters(prev => ({ 
      ...prev, 
      quickFilter: 'ozel', // Özel tarih aralığı seçildiğinde
      dateRange: { ...prev.dateRange, [field]: value } 
    }));
  };

  const handleSortChange = (field) => {
    setSortOrder(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const clearFilters = () => {
    setFilters({ 
      type: 'Hepsi', 
      category: '', 
      dateRange: { start: '', end: '' },
      quickFilter: 'tumZaman'
    });
  };

  // Filtrelenmiş tüm kayıtlar - tüm hesaplamalar için kullanılacak temel veri
  const filteredRecords = useMemo(() => {
    return records.filter(record => {
      // Mali kayıt tipleri için uygun filtre
      const typeMatch = filters.type === 'Hepsi' || 
                    (filters.type === 'Gelir' && record.type === 'Gelir') || 
                    (filters.type === 'Gider' && record.type === 'Gider');
      
      // Kategori veya açıklama üzerinden filtre
      const categoryMatch = !filters.category || 
                       (record.category && record.category.toLowerCase().includes(filters.category.toLowerCase()));
      
      // Tarih filtresi
      let startDateMatch = true;
      let endDateMatch = true;
      
      if (filters.dateRange.start && record.date instanceof Date) {
        const startDate = new Date(filters.dateRange.start);
        startDate.setHours(0, 0, 0, 0);
        startDateMatch = record.date >= startDate;
      }
      
      if (filters.dateRange.end && record.date instanceof Date) {
        const endDate = new Date(filters.dateRange.end);
        endDate.setHours(23, 59, 59, 999);
        endDateMatch = record.date <= endDate;
      }
      
      return typeMatch && categoryMatch && startDateMatch && endDateMatch;
    });
  }, [records, filters]);

  // Filtrelenmiş ve sıralanmış kayıtlar - tablo gösterimi için
  const filteredAndSortedRecords = useMemo(() => {
    return [...filteredRecords].sort((a, b) => {
      const fieldA = sortOrder.field === 'date' && a.date instanceof Date ? a.date : a[sortOrder.field];
      const fieldB = sortOrder.field === 'date' && b.date instanceof Date ? b.date : b[sortOrder.field];
      let comparison = 0;

      if (sortOrder.field === 'date') {
        // Date nesnelerini karşılaştır, değilse string olarak
        if (fieldA instanceof Date && fieldB instanceof Date) {
          comparison = fieldA.getTime() - fieldB.getTime();
        } else {
          // En azından bir tanesi Date değilse string karşılaştırması yap
          const aStr = fieldA instanceof Date ? fieldA.toISOString() : String(fieldA || '');
          const bStr = fieldB instanceof Date ? fieldB.toISOString() : String(fieldB || '');
          comparison = aStr.localeCompare(bStr);
        }
      } else if (sortOrder.field === 'amount') {
        comparison = parseFloat(fieldA || 0) - parseFloat(fieldB || 0);
      } else if (sortOrder.field === 'id') {
        // ID'ye göre sıralama sayısal olmalı
        comparison = parseInt(fieldA || 0) - parseInt(fieldB || 0);
      } else {
        // Diğer alanlar için string karşılaştırması
        const aStr = String(fieldA || '');
        const bStr = String(fieldB || '');
        comparison = aStr.localeCompare(bStr);
      }

      return sortOrder.direction === 'asc' ? comparison : -comparison;
    });
  }, [filteredRecords, sortOrder]);

  // Gelir kayıtları
  const incomeRecords = useMemo(() => {
    return filteredAndSortedRecords.filter(record => record.type === 'Gelir');
  }, [filteredAndSortedRecords]);

  // Gider kayıtları
  const expenseRecords = useMemo(() => {
    return filteredAndSortedRecords.filter(record => record.type === 'Gider');
  }, [filteredAndSortedRecords]);

  // Yardımcı parse fonksiyonları (Dashboard ile aynı)
  const parseDate = (dateString) => {
    try {
      if (dateString instanceof Date) return dateString;
      if (typeof dateString !== "string") return null;
      if (dateString.includes("GMT")) return new Date(dateString);
      const d = new Date(dateString);
      if (!isNaN(d.getTime())) return d;
      const parts = dateString.split(/[/.-]/);
      if (parts.length === 3) {
        if (parts[0].length <= 2) return new Date(parts[2], parts[1] - 1, parts[0]);
        if (parts[0].length === 4) return new Date(parts[0], parts[1] - 1, parts[2]);
      }
      return null;
    } catch (e) { return null; }
  };

  const parsePaymentPlan = (json) => {
    try {
      if (!json) return null;
      if (typeof json === "object") return json;
      let cleaned = json.replace(/\\"/g, '"').replace(/"\{/g, '{').replace(/\}"/g, '}');
      if (cleaned.startsWith('"') && cleaned.endsWith('"')) cleaned = cleaned.slice(1, -1);
      return JSON.parse(cleaned);
    } catch (e) { return null; }
  };

  // Dashboard verileri hesaplama - filtrelenmiş veriler üzerinden
  const dashboardData = useMemo(() => {
    console.log("💰 FINANCIAL DEBUG - Tüm kayıtlar:", filteredRecords.map(r => ({
      id: r.id,
      amount: r.amount,
      amount_type: typeof r.amount,
      parsed_with_parseFloat: parseFloat(r.amount),
      parsed_with_parseCurrency: parseCurrency(r.amount),
      description: r.description
    })));
    
    const totalIncome = filteredRecords
      .filter(r => {
        const amount = parseFloat(r.amount);
        return !isNaN(amount) && amount > 0;
      })
      .reduce((sum, r) => {
        const amount = parseFloat(r.amount);
        return sum + (isNaN(amount) ? 0 : amount);
      }, 0);
    
    const totalExpense = filteredRecords
      .filter(r => {
        const amount = parseFloat(r.amount);
        return !isNaN(amount) && amount < 0;
      })
      .reduce((sum, r) => {
        const amount = parseFloat(r.amount);
        return sum + (isNaN(amount) ? 0 : Math.abs(amount));
      }, 0);
    
    const netBalance = totalIncome - totalExpense;

    return { totalIncome, totalExpense, netBalance };
  }, [filteredRecords]);

  // Grafik verileri - filtrelenmiş veriler üzerinden
  const categoryChartData = useMemo(() => {
    const categoryTotals = filteredRecords.reduce((acc, record) => {
      // Kategori ve tür kontrolü
      if (!record.type || !record.category) {
        return acc;
      }
      
      // Gelir/Gider ve kategori üzerinden kategori oluştur
      const category = record.category || 'Diğer';
      const key = `${record.type}-${category}`;
      
      acc[key] = (acc[key] || 0) + Math.abs(parseFloat(record.amount));
      return acc;
    }, {});

    const incomeData = Object.entries(categoryTotals)
      .filter(([key]) => key.startsWith('Gelir-'))
      .map(([key, value]) => ({ category: key.split('-')[1], amount: value }))
      .sort((a, b) => b.amount - a.amount); // En yüksek değerden en düşüğe sırala

    const expenseData = Object.entries(categoryTotals)
      .filter(([key]) => key.startsWith('Gider-'))
      .map(([key, value]) => ({ category: key.split('-')[1], amount: value }))
      .sort((a, b) => b.amount - a.amount); // En yüksek değerden en düşüğe sırala

    // Renk paletleri
    const incomeColors = [
      '#4CAF50', '#8BC34A', '#CDDC39', '#FFEB3B', '#FFC107', 
      '#FF9800', '#FF5722', '#795548', '#9E9E9E', '#607D8B'
    ];
    
    const expenseColors = [
      '#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', 
      '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50'
    ];

    return {
      income: {
        labels: incomeData.map(d => d.category),
        datasets: [{
          data: incomeData.map(d => d.amount),
          backgroundColor: incomeColors.slice(0, incomeData.length),
          hoverBackgroundColor: incomeColors.slice(0, incomeData.length).map(color => color + 'CC'), // %80 opaklık
          borderWidth: 1,
          borderColor: '#fff'
        }]
      },
      expense: {
        labels: expenseData.map(d => d.category),
        datasets: [{
          data: expenseData.map(d => d.amount),
          backgroundColor: expenseColors.slice(0, expenseData.length),
          hoverBackgroundColor: expenseColors.slice(0, expenseData.length).map(color => color + 'CC'), // %80 opaklık
          borderWidth: 1,
          borderColor: '#fff'
        }]
      }
    };
  }, [filteredRecords]);

  // Aylık grafik verileri - filtrelenmiş veriler üzerinden
  const monthlyChartData = useMemo(() => {
    // Önce mevcut verileri birleştirelim
    const monthlyTotals = filteredRecords.reduce((acc, record) => {
      // Tarih kontrolü yap
      if (!record.date || !(record.date instanceof Date)) {
        console.log('Geçersiz tarih:', record);
        return acc;
      }
      
      // Ay-yıl formatında gruplama yap
      const monthYear = record.date.toLocaleDateString('tr-TR', { 
        year: 'numeric', 
        month: 'long' 
      });
      
      if (!acc[monthYear]) {
        acc[monthYear] = { income: 0, expense: 0 };
      }
      
      const amount = parseCurrency(record.amount);
      if (amount > 0) {
        acc[monthYear].income += amount;
      } else {
        acc[monthYear].expense += Math.abs(amount);
      }
      return acc;
    }, {});

    // Son 12 ayı oluştur (şu anki ayı da içerecek şekilde)
    const now = new Date();
    const last12Months = [];
    const monthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 
                       'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
    
    for (let i = 11; i >= 0; i--) {
      const month = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = monthNames[month.getMonth()];
      const yearName = month.getFullYear();
      const monthYearKey = `${monthName} ${yearName}`;
      
      last12Months.push({
        key: monthYearKey,
        month: month.getMonth(),
        year: yearName,
        date: month
      });
      
      // Eğer bu ay için veri yoksa boş veri ekle
      if (!monthlyTotals[monthYearKey]) {
        monthlyTotals[monthYearKey] = { income: 0, expense: 0 };
      }
    }
    
    // Son 12 ayı kronolojik sırayla göster (en son şu anki ay olacak)
    const sortedLabels = last12Months.map(item => item.key);

    return {
      labels: sortedLabels,
      datasets: [
        {
          label: 'Gelir',
          data: sortedLabels.map(label => monthlyTotals[label]?.income || 0),
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          tension: 0.3,
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          fill: true
        },
        {
          label: 'Gider',
          data: sortedLabels.map(label => monthlyTotals[label]?.expense || 0),
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.5)',
          tension: 0.3,
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          fill: true
        }
      ]
    };
  }, [filteredRecords]);

  // Grafik seçenekleri
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: 14
          },
          padding: 20
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#000',
        bodyColor: '#000',
        borderColor: '#ddd',
        borderWidth: 1,
        padding: 10,
        displayColors: true,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(context.parsed.y);
            }
            return label;
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(value);
          }
        }
      }
    }
  }), []);

  // Pasta grafik seçenekleri
  const pieChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          font: {
            size: 14
          },
          padding: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#000',
        bodyColor: '#000',
        borderColor: '#ddd',
        borderWidth: 1,
        padding: 10,
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = Math.round((value / total) * 100);
            return `${label}: ${new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value)} (${percentage}%)`;
          }
        }
      }
    }
  }), []);

  // Kayıt İşlemleri
  const handleSaveRecord = async (recordData) => {
    const token = localStorage.getItem("authToken");
    
    // API'ye gönderilecek veriyi hazırla
    const apiData = {
      description: recordData.description,
      amount: recordData.amount,
      transaction_date: recordData.transaction_date,
      payment_method: recordData.payment_method,
      status: recordData.status || 'completed',
      category: recordData.category || 'Diğer', // Kategori alanını ekle
      notes: recordData.notes
    };
    
    // REST API endpoint'leri
    const apiEndpoint = recordData.id
        ? `http://esnakademi.com/api/financial-records/${recordData.id}`
      : `http://esnakademi.com/api/financial-records`;
    const method = recordData.id ? 'put' : 'post';

    try {
      setLoading(true);
      console.log("Gönderilen veri:", apiData);
      
      const response = await axios({
        method,
        url: apiEndpoint,
        data: apiData,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log("API yanıtı:", response.data);

      if (recordData.id) {
        // Güncelleme işlemi
        setRecords(records.map(r => r.id === recordData.id ? 
          { 
            ...apiData, 
            id: recordData.id,
            date: new Date(apiData.transaction_date),
            type: parseFloat(apiData.amount) >= 0 ? 'Gelir' : 'Gider',
            category: apiData.category,
            // Amount'u orijinal değerini koruyarak formatla
            amount: parseFloat(apiData.amount)
          } : r
        ));
        toast.success("Kayıt başarıyla güncellendi!");
      } else {
        // Yeni kayıt işlemi - cevaptaki ID'yi alıp listeye ekle
        const newId = response.data.id || Date.now(); // Eğer API ID dönmezse geçici bir ID kullan
        const newRecord = { 
          ...apiData,
          id: newId,
          date: new Date(apiData.transaction_date),
          type: parseFloat(apiData.amount) >= 0 ? 'Gelir' : 'Gider',
          category: apiData.category,
                      // Amount'u orijinal değerini koruyarak formatla
            amount: parseFloat(apiData.amount)
        };
        setRecords([...records, newRecord]);
        toast.success("Kayıt başarıyla eklendi!");
      }
      closeModal();
    } catch (err) {
      console.error("Kayıt kaydetme hatası:", err);
      toast.error("Kayıt kaydedilemedi!");
    } finally {
      setLoading(false);
    }
  };

  const deleteRecord = async (id) => {
    if (!window.confirm('Bu kaydı silmek istediğinize emin misiniz? Bu işlem geri alınamaz ve öğrenci ödeme planından da silinecektir.')) {
      return;
    }

    const loadingToast = toast.loading('Kayıt siliniyor...');

    try {
      // Local storage'dan token al
      const token = localStorage.getItem('authToken');
      
      // Authorization başlığı için headers hazırla
      const headers = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else {
        console.error('Token bulunamadı! Lütfen giriş yapın.');
        toast.error('Oturum süresi dolmuş olabilir. Lütfen tekrar giriş yapın.');
        return;
      }

      // 1. Önce silinecek kaydın detaylarını al
      const recordToDelete = records.find(record => record.id === id);
      
      if (!recordToDelete) {
        toast.error('Silinecek kayıt bulunamadı');
        return;
      }

      // 2. Financial record'u sil
      await axios.delete(`http://esnakademi.com/api/financial-records?id=${id}`, { headers });

      // 3. Eğer bu bir öğrenci ödemesi ise, öğrenci payment_plan'ından da sil
      if (recordToDelete.description && recordToDelete.amount > 0) {
        try {
          // Öğrenci listesini al
          const timestamp3 = new Date().getTime();
          const studentsResponse = await axios.get(`http://esnakademi.com/api/students?_t=${timestamp3}`, { headers });
          const studentsList = studentsResponse.data || [];

          // İlgili öğrenciyi bul (description'da öğrenci adı var)
          // Description formatı: "Ad Soyad - Taksit Ödemesi" veya "Ad Soyad - Nakit ödeme"
          const relatedStudent = studentsList.find(student => {
            const studentName = `${student.name} ${student.surname}`;
            return recordToDelete.description.includes(studentName) && 
                   (recordToDelete.description.includes("Taksit Ödemesi") || 
                    recordToDelete.description.includes("Nakit ödeme"));
          });

          if (relatedStudent) {
            // Payment plan'ı parse et
            let paymentPlan;
            try {
              paymentPlan = typeof relatedStudent.payment_plan === 'string' 
                ? JSON.parse(relatedStudent.payment_plan) 
                : relatedStudent.payment_plan;
            } catch (e) {
              console.warn('Payment plan parse edilemedi:', e);
              paymentPlan = null;
            }

            if (paymentPlan?.paidInstallments && paymentPlan.paidInstallments.length > 0) {
              // Silinen ödemeyi bul (tutar ve tarih eşleştirmesi)
              const recordAmount = Math.abs(parseFloat(recordToDelete.amount));
              const recordDate = recordToDelete.transaction_date;



              // Silinen ödemeyi bul - önce kesin eşleşme dene
              let installmentToDelete = paymentPlan.paidInstallments.find(paid => {
                const paidAmount = parseFloat(paid.amount);
                const paidDate = paid.paymentDate;
                const amountMatch = Math.abs(paidAmount - recordAmount) < 0.01;
                const dateMatch = paidDate === recordDate;
                
                return amountMatch && dateMatch;
              });
              
              // Kesin eşleşme bulunamazsa, partial payment'ları kontrol et
              if (!installmentToDelete) {
                // Aynı tarihte toplam tutarı eşleşen partial payment'ları bul
                const sameDate = paymentPlan.paidInstallments.filter(paid => 
                  paid.paymentDate === recordDate
                );
                
                const totalSameDateAmount = sameDate.reduce((total, paid) => 
                  total + parseFloat(paid.amount), 0
                );
                
                // Toplam tutar eşleşiyorsa, TÜM partial payment'ları sil
                if (Math.abs(totalSameDateAmount - recordAmount) < 0.01 && sameDate.length > 1) {
                  // Bu bir partial payment grubu - hepsini sil
                  const updatedPaymentPlan = { ...paymentPlan };
                  
                  // Aynı tarihteki tüm payment'ları çıkar
                  updatedPaymentPlan.paidInstallments = updatedPaymentPlan.paidInstallments.filter(paid => 
                    paid.paymentDate !== recordDate
                  );
                  
                  // Silinen partial payment'ların tutarlarını unpaidBills'e geri ekle
                  sameDate.forEach(partialPayment => {
                    if (partialPayment.appliedAmounts && Array.isArray(partialPayment.appliedAmounts)) {
                      partialPayment.appliedAmounts.forEach(applied => {
                        const bill = updatedPaymentPlan.unpaidBills.find(b => b.id === applied.installmentId);
                        if (bill) {
                          bill.amount = (parseFloat(bill.amount) + applied.amount).toFixed(2);
                        }
                      });
                    }
                  });
                  
                  // Öğrenci verisini güncelle
                  const paymentPlanJson = JSON.stringify(updatedPaymentPlan);
                  await axios.put(`http://esnakademi.com/api/students/update?id=${relatedStudent.id}`, {
                    id: relatedStudent.id,
                    payment_plan: paymentPlanJson
                  }, { headers });
                  

                  return; // Early return - normal silme işlemini atla
                }
              }

              if (installmentToDelete) {
                // Payment plan kopyası oluştur
                const updatedPaymentPlan = { ...paymentPlan };
                
                // Kısmi ödeme kontrolü
                const isPartialPayment = installmentToDelete.partialPayment || installmentToDelete.originalInstallmentId;

                if (isPartialPayment) {
                  // appliedAmounts bilgisi varsa, her taksite doğru tutarı iade et
                  if (installmentToDelete.appliedAmounts && Array.isArray(installmentToDelete.appliedAmounts)) {
                    // Mevcut unpaidBills'de orijinal taksit var mı kontrol et
                    if (!updatedPaymentPlan.unpaidBills) {
                      updatedPaymentPlan.unpaidBills = [];
                    }
                    
                    // Her uygulanan tutarı ilgili taksite iade et
                    installmentToDelete.appliedAmounts.forEach(applied => {
                      const existingUnpaidIndex = updatedPaymentPlan.unpaidBills.findIndex(bill => bill.id === applied.installmentId);
                      
                      if (existingUnpaidIndex >= 0) {
                        // Mevcut taksite tutarı ekle
                        const currentAmount = parseFloat(updatedPaymentPlan.unpaidBills[existingUnpaidIndex].amount);
                        updatedPaymentPlan.unpaidBills[existingUnpaidIndex].amount = currentAmount + parseFloat(applied.amount);
                      } else {
                        // Bu taksit için unpaid bill yoksa, yeni oluştur
                        const restoredBill = {
                          id: applied.installmentId,
                          amount: parseFloat(applied.amount),
                          dueDate: installmentToDelete.originalDueDate || installmentToDelete.dueDate || installmentToDelete.paymentDate,
                          notes: `${applied.installmentId}. Taksit`
                        };
                        updatedPaymentPlan.unpaidBills.push(restoredBill);
                      }
                    });
                  } else {
                    // Eski yöntem - geriye uyumluluk için
                    const originalInstallmentId = installmentToDelete.originalInstallmentId || installmentToDelete.id;
                    
                    // Mevcut unpaidBills'de orijinal taksit var mı kontrol et
                    if (!updatedPaymentPlan.unpaidBills) {
                      updatedPaymentPlan.unpaidBills = [];
                    }
                    
                    const existingUnpaidIndex = updatedPaymentPlan.unpaidBills.findIndex(bill => bill.id === originalInstallmentId);
                    
                    if (existingUnpaidIndex >= 0) {
                      // Mevcut unpaid bill'in tutarını artır
                      const currentAmount = parseFloat(updatedPaymentPlan.unpaidBills[existingUnpaidIndex].amount);
                      const deletedAmount = parseFloat(installmentToDelete.amount);
                      const newAmount = currentAmount + deletedAmount;
                      
                      // Orijinal taksit tutarını kontrol et
                      const originalAmount = parseFloat(installmentToDelete.originalAmount || installmentToDelete.amount);
                      
                      // Eğer yeni tutar orijinal taksit tutarını aşıyorsa, fazla kısmı sonraki taksitlere dağıt
                      if (newAmount > originalAmount) {
                        const excessAmount = newAmount - originalAmount;
                        
                        // Mevcut taksiti orijinal tutarına ayarla
                        updatedPaymentPlan.unpaidBills[existingUnpaidIndex].amount = originalAmount;
                        
                        // Fazla tutarı sonraki taksitlere dağıt
                        let remainingExcess = excessAmount;
                        const nextBills = updatedPaymentPlan.unpaidBills
                          .filter(bill => bill.id > originalInstallmentId)
                          .sort((a, b) => a.id - b.id);
                        
                        for (const nextBill of nextBills) {
                          if (remainingExcess <= 0) break;
                          
                          const nextBillAmount = parseFloat(nextBill.amount);
                          if (remainingExcess >= nextBillAmount) {
                            // Bu taksiti tamamen kapat
                            nextBill.amount = 0;
                            remainingExcess -= nextBillAmount;
                          } else {
                            // Bu taksiti kısmen azalt
                            nextBill.amount = nextBillAmount - remainingExcess;
                            remainingExcess = 0;
                          }
                        }
                        
                        // Sıfır tutarlı taksitleri kaldır
                        updatedPaymentPlan.unpaidBills = updatedPaymentPlan.unpaidBills.filter(bill => parseFloat(bill.amount) > 0);
                      } else {
                        // Normal durum - sadece tutarı artır
                        updatedPaymentPlan.unpaidBills[existingUnpaidIndex].amount = newAmount;
                      }
                    } else {
                      // Yeni unpaid bill oluştur
                      const restoredBill = {
                        id: originalInstallmentId,
                        amount: installmentToDelete.amount,
                        dueDate: installmentToDelete.originalDueDate || installmentToDelete.dueDate || installmentToDelete.paymentDate,
                        notes: installmentToDelete.notes || `${originalInstallmentId}. Taksit`
                      };
                      
                      updatedPaymentPlan.unpaidBills.push(restoredBill);
                    }
                  }
                  
                  // Tarih sırasına göre sırala
                  updatedPaymentPlan.unpaidBills.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
                } else {
                  // Normal taksit durumu - tamamen unpaidBills'e geri ekle
                  if (!updatedPaymentPlan.unpaidBills) {
                    updatedPaymentPlan.unpaidBills = [];
                  }
                  
                  const restoredBill = {
                    id: installmentToDelete.id,
                    amount: installmentToDelete.amount,
                    dueDate: installmentToDelete.dueDate || installmentToDelete.paymentDate,
                    notes: installmentToDelete.notes || `${installmentToDelete.id}. Taksit`
                  };
                  
                  updatedPaymentPlan.unpaidBills.push(restoredBill);
                }

                // Unpaid bills'i tarih sırasına göre sırala
                updatedPaymentPlan.unpaidBills.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

                // Silinen ödemeyi paidInstallments'tan kaldır
                updatedPaymentPlan.paidInstallments = updatedPaymentPlan.paidInstallments.filter(paid => 
                  !(Math.abs(parseFloat(paid.amount) - recordAmount) < 0.01 && paid.paymentDate === recordDate)
                );

                // Öğrenciyi güncelle
                await axios.put(
                  `http://esnakademi.com/api/students/update?id=${relatedStudent.id}`,
                  {
                    ...relatedStudent,
                    payment_plan: JSON.stringify(updatedPaymentPlan)
                  },
                  { headers }
                );
              }
            }
          }
        } catch (studentError) {
          console.warn('Öğrenci payment plan güncellenirken hata (devam edildi):', studentError);
          // Financial record silindi ama student update başarısız - bu warning olarak devam et
        }
      }

      toast.dismiss(loadingToast);
      toast.success('Kayıt başarıyla silindi');
      
      // State'ten silinen kaydı kaldır (daha hızlı güncelleme)
      setRecords(prevRecords => prevRecords.filter(record => record.id !== id));
      
      // Dashboard'ı güncellemek için global event gönder
      window.dispatchEvent(new CustomEvent('paymentDataChanged'));
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Kayıt silinirken hata:', error);
      toast.error(`Kayıt silinemedi: ${error.response?.data?.message || error.message}`);
    }
  };

  const openModalForNew = () => {
    setEditingRecord(null);
    setIsModalOpen(true);
  };

  const openModalForEdit = (record) => {
    setEditingRecord(record);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingRecord(null);
  };

  // Yükleme ve Hata Durumları
  if (loading && records.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Mali veriler yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <div className="text-red-500 text-center text-5xl mb-4">
            <FaMoneyBillWave className="mx-auto" />
        </div>
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-4">Veri Yükleme Hatası</h2>
          <p className="text-gray-600 mb-6 text-center">{error}</p>
          <button
            onClick={fetchRecords}
            className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-6 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Başlık ve Yeni Kayıt Butonu */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
          <h1 className="text-3xl font-bold text-gray-800 mb-4 sm:mb-0">
            Mali Kayıtlar Yönetimi
          </h1>
          <button
            onClick={openModalForNew}
            className="bg-gradient-to-r from-blue-600 to-blue-800 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-blue-900 transition-all duration-300 shadow-md flex items-center"
          >
            <FaPlus className="mr-2" /> Yeni Kayıt Ekle
          </button>
        </div>

        {/* Aktif Filtre Gösterimi */}
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-6 flex items-center justify-between">
          <div className="flex items-center">
            <FaFilter className="text-blue-600 mr-2" />
            <div>
              <span className="text-sm text-blue-700">Aktif Filtre:</span>
              <span className="ml-2 font-semibold text-blue-800">{getActiveFilterText()}</span>
              {filters.type !== 'Hepsi' && <span className="ml-2 bg-blue-200 text-blue-800 px-2 py-0.5 rounded text-xs">{filters.type}</span>}
              {filters.category && <span className="ml-2 bg-purple-200 text-purple-800 px-2 py-0.5 rounded text-xs">"{filters.category}"</span>}
            </div>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center text-sm px-3 py-1 rounded-lg border transition-all duration-300 ${
              showFilters ? "border-blue-500 bg-blue-100 text-blue-700" : "border-blue-300 bg-white text-blue-600"
            }`}
          >
            {showFilters ? "Filtreleri Gizle" : "Filtreleri Göster"}
            {showFilters ? <FaChevronUp className="ml-1" /> : <FaChevronDown className="ml-1" />}
          </button>
        </div>

        {/* Genişletilebilir Filtre Bölümü */}
        {showFilters && (
          <div className="bg-white rounded-xl shadow-md p-4 mb-6 transition-all duration-300 ease-in-out">
            {/* Hızlı Filtre Butonları */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Hızlı Tarih Filtreleri</label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => applyQuickDateFilter('bugun')}
                  className={`px-3 py-1.5 rounded-lg border text-sm ${
                    filters.quickFilter === 'bugun' 
                      ? 'bg-blue-600 text-white border-blue-600' 
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Bugün
                </button>
                <button
                  onClick={() => applyQuickDateFilter('buAy')}
                  className={`px-3 py-1.5 rounded-lg border text-sm ${
                    filters.quickFilter === 'buAy' 
                      ? 'bg-blue-600 text-white border-blue-600' 
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Bu Ay
                </button>
                <button
                  onClick={() => applyQuickDateFilter('son3Ay')}
                  className={`px-3 py-1.5 rounded-lg border text-sm ${
                    filters.quickFilter === 'son3Ay' 
                      ? 'bg-blue-600 text-white border-blue-600' 
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Son 3 Ay
                </button>
                <button
                  onClick={() => applyQuickDateFilter('son6Ay')}
                  className={`px-3 py-1.5 rounded-lg border text-sm ${
                    filters.quickFilter === 'son6Ay' 
                      ? 'bg-blue-600 text-white border-blue-600' 
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Son 6 Ay
                </button>
                <button
                  onClick={() => applyQuickDateFilter('tumZaman')}
                  className={`px-3 py-1.5 rounded-lg border text-sm ${
                    filters.quickFilter === 'tumZaman' 
                      ? 'bg-blue-600 text-white border-blue-600' 
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Tüm Zamanlar
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tür</label>
                <select
                  name="type"
                  value={filters.type}
                  onChange={handleFilterChange}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Hepsi">Hepsi</option>
                  <option value="Gelir">Gelir</option>
                  <option value="Gider">Gider</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kategori Ara</label>
                <input
                  type="text"
                  name="category"
                  placeholder="Kategori adı..."
                  value={filters.category}
                  onChange={handleFilterChange}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Başlangıç Tarihi</label>
                <input
                  type="date"
                  value={filters.dateRange.start}
                  onChange={(e) => handleDateRangeChange('start', e.target.value)}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  min="1900-01-01"
                  max="2099-12-31"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bitiş Tarihi</label>
                <input
                  type="date"
                  value={filters.dateRange.end}
                  onChange={(e) => handleDateRangeChange('end', e.target.value)}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  min="1900-01-01"
                  max="2099-12-31"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={clearFilters}
                className="py-2 px-4 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Filtreleri Temizle
              </button>
            </div>
          </div>
        )}



        {/* Dashboard Göstergeleri */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4">
                <FaArrowUp size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Toplam Gelir</p>
                <h3 className="text-2xl font-bold text-gray-800">
                  {new Intl.NumberFormat("tr-TR", {
                    style: "currency",
                    currency: "TRY",
                  }).format(dashboardData.totalIncome)}
                </h3>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-red-100 text-red-600 mr-4">
                <FaArrowDown size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Toplam Gider</p>
                <h3 className="text-2xl font-bold text-gray-800">
                  {new Intl.NumberFormat("tr-TR", {
                    style: "currency",
                    currency: "TRY",
                  }).format(dashboardData.totalExpense)}
                </h3>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
            <div className="flex items-center">
              <div className={`p-3 rounded-full mr-4 ${
                dashboardData.netBalance >= 0 ? 'bg-blue-100 text-blue-600' : 'bg-orange-100 text-orange-600'
              }`}>
                <FaMoneyBillWave size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Net Bakiye</p>
                <h3 className="text-2xl font-bold text-gray-800">
                  {new Intl.NumberFormat("tr-TR", {
                    style: "currency",
                    currency: "TRY",
                  }).format(dashboardData.netBalance)}
                </h3>
              </div>
            </div>
          </div>
        </div>

        {/* Grafikler */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <FaChartPie className="mr-2 text-purple-600" /> Kategori Dağılımı (Gelir)
            </h2>
            {categoryChartData.income.labels.length > 0 ? (
              <div className="h-[350px]">
                <Pie data={categoryChartData.income} options={pieChartOptions} />
              </div>
            ) : <p className="text-center text-gray-500">Gelir kaydı bulunamadı.</p>}
          </div>
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <FaChartPie className="mr-2 text-red-600" /> Kategori Dağılımı (Gider)
            </h2>
            {categoryChartData.expense.labels.length > 0 ? (
              <div className="h-[350px]">
                <Pie data={categoryChartData.expense} options={pieChartOptions} />
              </div>
            ) : <p className="text-center text-gray-500">Gider kaydı bulunamadı.</p>}
          </div>
          <div className="bg-white rounded-xl shadow-md p-6 lg:col-span-2">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <FaChartLine className="mr-2 text-indigo-600" /> Aylık Gelir/Gider Trendi
            </h2>
            {monthlyChartData.labels.length > 0 ? (
              <div className="h-[400px]">
                <Line data={monthlyChartData} options={chartOptions} />
              </div>
            ) : <p className="text-center text-gray-500">Trend grafiği için yeterli veri bulunamadı.</p>}
          </div>
        </div>

        {/* Gelir ve Gider Tabloları */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Gelir Tablosu */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-4 md:p-6">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <h2 className="text-xl font-semibold text-green-800 flex items-center">
                  <FaArrowUp className="mr-2" />
                  Gelir Listesi
                </h2>
                {incomeRecords.length > 0 && (
                  <div className="text-sm text-gray-500">
                    {incomeRecords.length} kayıt
                  </div>
                )}
              </div>
            </div>

            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-green-50">
                  <tr>
                    {[
                      { label: 'Kategori', field: 'category' },
                      { label: 'Tutar', field: 'amount' },
                      { label: 'Tarih', field: 'date' },
                      { label: 'Açıklama', field: 'description' }
                    ].map(header => (
                      <th
                        key={header.field}
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-green-100"
                        onClick={() => handleSortChange(header.field)}
                      >
                        <div className="flex items-center">
                          {header.label}
                          {sortOrder.field === header.field && (
                            <span className="ml-2">
                              {sortOrder.direction === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      İşlemler
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {incomeRecords.length > 0 ? (
                    incomeRecords.map((record) => (
                      <tr key={record.id} className="hover:bg-green-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{record.category}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                          ₺{formatCurrency(Math.abs(parseFloat(record.amount)))}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {record.date instanceof Date 
                            ? record.date.toLocaleDateString('tr-TR') 
                            : (record.transaction_date 
                                ? new Date(record.transaction_date).toLocaleDateString('tr-TR') 
                                : '-')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-xs truncate" title={record.description}>{record.description || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                          <button
                            onClick={() => openModalForEdit(record)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Düzenle"
                          >
                            <FaEdit />
                          </button>
                          {/* Öğrenci ödemelerini silme - sadece öğrenci detayından silinebilir */}
                          {record.description && 
                           (record.description.includes("Taksit Ödemesi") || 
                            record.description.includes("Nakit ödeme") ||
                            record.description.includes("Kısmi Ödeme")) ? (
                            <button
                              className="text-gray-400 cursor-not-allowed"
                              title="Öğrenci ödemeleri sadece öğrenci detay sayfasından silinebilir"
                              disabled
                            >
                              <FaTrash />
                            </button>
                          ) : (
                            <button
                              onClick={() => deleteRecord(record.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Sil"
                            >
                              <FaTrash />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                        Gelir kaydı bulunamadı.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Gider Tablosu */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-4 md:p-6">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <h2 className="text-xl font-semibold text-red-800 flex items-center">
                  <FaArrowDown className="mr-2" />
                  Gider Listesi
                </h2>
                {expenseRecords.length > 0 && (
                  <div className="text-sm text-gray-500">
                    {expenseRecords.length} kayıt
                  </div>
                )}
              </div>
            </div>

            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-red-50">
                  <tr>
                    {[
                      { label: 'Kategori', field: 'category' },
                      { label: 'Tutar', field: 'amount' },
                      { label: 'Tarih', field: 'date' },
                      { label: 'Açıklama', field: 'description' }
                    ].map(header => (
                      <th
                        key={header.field}
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-red-100"
                        onClick={() => handleSortChange(header.field)}
                      >
                        <div className="flex items-center">
                          {header.label}
                          {sortOrder.field === header.field && (
                            <span className="ml-2">
                              {sortOrder.direction === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      İşlemler
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {expenseRecords.length > 0 ? (
                    expenseRecords.map((record) => (
                      <tr key={record.id} className="hover:bg-red-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{record.category}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                          ₺{formatCurrency(Math.abs(parseFloat(record.amount)))}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {record.date instanceof Date 
                            ? record.date.toLocaleDateString('tr-TR') 
                            : (record.transaction_date 
                                ? new Date(record.transaction_date).toLocaleDateString('tr-TR') 
                                : '-')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-xs truncate" title={record.description}>{record.description || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                          <button
                            onClick={() => openModalForEdit(record)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Düzenle"
                          >
                            <FaEdit />
                          </button>
                          {/* Öğrenci ödemelerini silme - sadece öğrenci detayından silinebilir */}
                          {record.description && 
                           (record.description.includes("Taksit Ödemesi") || 
                            record.description.includes("Nakit ödeme") ||
                            record.description.includes("Kısmi Ödeme")) ? (
                            <button
                              className="text-gray-400 cursor-not-allowed"
                              title="Öğrenci ödemeleri sadece öğrenci detay sayfasından silinebilir"
                              disabled
                            >
                              <FaTrash />
                            </button>
                          ) : (
                            <button
                              onClick={() => deleteRecord(record.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Sil"
                            >
                              <FaTrash />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                        Gider kaydı bulunamadı.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Kayıt Ekle/Düzenle Modalı */}
      <RecordModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSave={handleSaveRecord}
        record={editingRecord}
      />
    </div>
  );
};

export default FinancialPage;
